import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SalaryRevisionTimeline from "@/components/SalaryRevisionTimeline";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Download, Plus, Trash2, FileText, AlertCircle,
} from "lucide-react";

const COMPONENT_ROWS = [
  // [key, label, section]   sections: earn | gross | empContrib | ctc | ded | net
  ["basic", "Basic", "earn"],
  ["da", "DA (Dearness Allowance)", "earn"],
  ["hra", "HRA (House Rent Allowance)", "earn"],
  ["ta", "TA (Transport Allowance)", "earn"],
  ["medical_allowance", "Medical Allowance", "earn"],
  ["retention_incentive", "Retention Incentive", "earn"],
  ["other_allowances", "Other Allowances", "earn"],
  ["flexi_basket", "Flexi Basket", "earn"],
  ["statutory_bonus_advance", "Statutory Bonus Advance", "earn"],
  ["gross_salary", "GROSS SALARY (auto)", "gross"],
  ["employer_pf_contribution", "Employer PF Contribution", "empContrib"],
  ["employer_esic_contribution", "Employer ESIC Contribution", "empContrib"],
  ["statutory_bonus", "Statutory Bonus", "empContrib"],
  ["ctc", "CTC (auto)", "ctc"],
  ["employee_pf_contribution", "Employee PF Contribution", "ded"],
  ["employee_esic_contribution", "Employee ESIC Contribution", "ded"],
  ["less_retention_incentive", "Less: Retention Incentive", "ded"],
  ["gmc_policy", "GMC Policy", "ded"],
  ["net_payable_monthly", "NET PAYABLE (MONTHLY) (auto)", "net"],
];

const ROW_NUMBERS = {};
COMPONENT_ROWS.forEach(([, , ], idx) => {});
COMPONENT_ROWS.forEach(([key], idx) => { ROW_NUMBERS[key] = idx + 1; });

const AUTO_KEYS = new Set(["gross_salary", "ctc", "total_deductions", "net_payable_monthly"]);

const REVISION_TYPES = [
  { value: "new_joining", label: "New Joining" },
  { value: "annual_increment", label: "Annual Increment" },
  { value: "promotion", label: "Promotion" },
  { value: "performance_based", label: "Performance-based" },
  { value: "role_change", label: "Role Change" },
  { value: "correction", label: "Correction" },
  { value: "format_revision", label: "Format Revision" },
];

const REV_LABEL = Object.fromEntries(REVISION_TYPES.map((r) => [r.value, r.label]));

function fmtINR(n) {
  if (n === null || n === undefined || n === "" || isNaN(Number(n))) return "—";
  const num = Number(n);
  if (num === 0) return "—";
  return "₹" + num.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function calcTotals(values) {
  const f = (k) => parseFloat(values[k] || 0) || 0;
  const gross = f("basic") + f("da") + f("hra") + f("ta") + f("medical_allowance")
    + f("retention_incentive") + f("other_allowances") + f("flexi_basket")
    + f("statutory_bonus_advance");
  const employer = f("employer_pf_contribution") + f("employer_esic_contribution") + f("statutory_bonus");
  const ctc = gross + employer;
  const deductions = f("employee_pf_contribution") + f("employee_esic_contribution")
    + f("less_retention_incentive") + f("gmc_policy")
    + f("professional_tax") + f("income_tax") + f("other_deductions");
  const net = gross - deductions;
  return { gross_salary: gross, ctc, total_deductions: deductions, net_payable_monthly: net };
}

export default function EmployeeMasterRecord() {
  const { id } = useParams(); // employee id
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [records, setRecords] = useState([]);
  const [baseline, setBaseline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  // Form for new revision
  const initialForm = useMemo(() => ({
    revision_description: "annual_increment",
    revision_reason: "",
    effective_from_date: new Date().toISOString().slice(0, 10),
    next_appraisal_due_date: "",
    flexi_basket_notes: "",
    additional_notes: "",
    percentage: "",
    gift_amount: "",
    gift_description: "",
    net_target_mode: "off",
    net_target_value: "",
    ...Object.fromEntries(COMPONENT_ROWS.map(([k]) => [k, ""])),
  }), []);
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ data: emp }, { data: recs }, baselineResp] = await Promise.all([
        api.get(`/employees/${id}`),
        api.get(`/employees/${id}/master-records`),
        api.get(`/employees/${id}/master-records/baseline`).catch(() => ({ data: null })),
      ]);
      setEmployee(emp);
      setRecords(recs || []);
      setBaseline(baselineResp.data);
      // Pick the newest record by default
      if (recs && recs.length > 0 && !selectedId) {
        setSelectedId(recs[0].id);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  const selected = records.find((r) => r.id === selectedId);
  const previous = selected?.previous_record_id
    ? records.find((r) => r.id === selected.previous_record_id)
    : null;

  // Auto-totals for the form preview
  const formTotals = useMemo(() => calcTotals(form), [form]);

  const openNewRevision = () => {
    // Prefill from latest active record OR baseline (computed from CTC)
    const source = records[0] || baseline;
    const next = { ...initialForm };
    if (source) {
      COMPONENT_ROWS.forEach(([k]) => {
        if (!AUTO_KEYS.has(k)) next[k] = source[k] ?? "";
      });
    }
    // Default next appraisal due = effective_from + 12 months
    try {
      const d = new Date(next.effective_from_date);
      d.setFullYear(d.getFullYear() + 1);
      next.next_appraisal_due_date = d.toISOString().slice(0, 10);
    } catch (_) { /* ignore */ }
    setForm(next);
    setShowDialog(true);
  };

  const applyPercentage = () => {
    const pct = parseFloat(form.percentage);
    if (!pct || pct <= 0) return toast.error("Enter a positive percentage");
    const source = records[0] || baseline;
    if (!source) return toast.error("No baseline to apply percentage on");
    const next = { ...form };
    COMPONENT_ROWS.forEach(([k]) => {
      if (AUTO_KEYS.has(k)) return;
      const base = parseFloat(source[k] || 0) || 0;
      next[k] = Math.round(base * (1 + pct / 100));
    });
    setForm(next);
    toast.success(`Applied +${pct}% to all components`);
  };

  const submit = async () => {
    if (!form.effective_from_date) return toast.error("Effective date required");
    setBusy(true);
    try {
      const payload = {
        revision_description: form.revision_description,
        revision_reason: form.revision_reason || null,
        effective_from_date: form.effective_from_date,
        next_appraisal_due_date: form.next_appraisal_due_date || null,
        flexi_basket_notes: form.flexi_basket_notes || null,
        additional_notes: form.additional_notes || null,
        gift_amount: parseFloat(form.gift_amount || 0) || 0,
        gift_description: form.gift_description || null,
      };
      COMPONENT_ROWS.forEach(([k]) => {
        if (!AUTO_KEYS.has(k)) payload[k] = parseFloat(form[k] || 0) || 0;
      });
      const { data } = await api.post(`/employees/${id}/master-records`, payload);
      toast.success(`Revision ${data.revision_number} saved as ${data.record_number}`);
      setShowDialog(false);
      setSelectedId(data.id);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  const downloadPdf = async (rid) => {
    try {
      const resp = await api.get(`/master-records/${rid}/pdf`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `MasterRecord_${selected?.employee_code || rid}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF download failed");
    }
  };

  const remove = async (rid) => {
    if (!window.confirm("Delete this master record? Previous record will be restored as active.")) return;
    try {
      await api.delete(`/master-records/${rid}`);
      toast.success("Record deleted");
      if (selectedId === rid) setSelectedId(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  if (loading) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto space-y-4" data-testid="employee-master-record">
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Master Record</div>
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            {employee?.name || "—"} <span className="text-slate-400 text-sm font-normal">({employee?.punch_id || employee?.employee_code})</span>
          </h1>
        </div>
        <Button onClick={openNewRevision} className="brand-bg brand-bg-hover text-white" data-testid="new-revision-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> New Revision
        </Button>
      </div>

      {/* Phase B Session 7 — Approval-based Salary Revision Timeline (auto-hides if no revisions) */}
      <SalaryRevisionTimeline employeeId={id} />

      {/* Revisions history strip */}
      <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid="revisions-strip">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Revisions</div>
        {records.length === 0 ? (
          <div className="text-sm text-slate-500 py-3 text-center">
            No master records yet. Click "New Revision" to create the first one (Rev 00 — New Joining).
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {records.map((r) => (
              <button
                key={r.id}
                onClick={() => setSelectedId(r.id)}
                className={`text-left rounded border p-2 text-xs transition ${
                  selectedId === r.id ? "border-red-400 bg-red-50/60" : "border-slate-200 bg-white hover:border-slate-300"
                }`}
                data-testid={`revision-${r.revision_number}`}
              >
                <div className="font-mono text-[10px] text-slate-500">{r.record_number}</div>
                <div className="font-semibold">Rev {String(r.revision_number).padStart(2, "0")}</div>
                <div className="text-[10px] text-slate-500">{REV_LABEL[r.revision_description] || r.revision_description}</div>
                <div className="text-[10px] text-slate-500">{r.effective_from_date}</div>
                {r.record_status === "active" && (
                  <span className="inline-block mt-1 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold">CURRENT</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected record full view (same format as PDF) */}
      {selected && (
        <div className="bg-white border-2 border-slate-300 rounded-lg p-6" data-testid="record-view">
          <div className="text-center pb-3 border-b-2 border-slate-300">
            <div className="text-base font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
              BRAVURA PACKERS PVT LTD
            </div>
            <div className="text-sm text-slate-700 mt-0.5">EMPLOYEE MASTER RECORD</div>
            <div className="text-xs text-slate-500 mt-1">
              <span className="font-mono">{selected.record_number}</span> · Rev {String(selected.revision_number).padStart(2, "0")} · Effective {selected.effective_from_date}
              {selected.next_appraisal_due_date && (
                <span className="ml-2 text-red-700 font-semibold">· Next Appraisal: {selected.next_appraisal_due_date}</span>
              )}
              {selected.record_status === "superseded" && (
                <span className="ml-2 text-amber-700 font-semibold">(SUPERSEDED on {selected.effective_to_date})</span>
              )}
            </div>
          </div>

          {/* Info grid */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs mt-4 pb-4 border-b border-slate-200">
            <InfoRow label="Employee Code" value={selected.employee_code} />
            <InfoRow label="Designation" value={selected.designation} />
            <InfoRow label="Employee Name" value={selected.employee_name} />
            <InfoRow label="Department" value={selected.department} />
            <InfoRow label="Date of Joining" value={selected.date_of_joining} />
            <InfoRow label="PAN" value={selected.pan_number} />
            <InfoRow label="Date of Birth" value={selected.date_of_birth} />
            <InfoRow label="Aadhaar No" value={selected.aadhaar_number} />
            <InfoRow label="F./H. Name" value={selected.father_or_husband_name} />
            <InfoRow label="Bank A/c No" value={selected.bank_account_no} />
            <InfoRow label="Bank Name" value={selected.bank_name} />
            <InfoRow label="IFSC Code" value={selected.ifsc_code} />
          </div>

          {/* Salary table */}
          <div className="mt-4 overflow-hidden border border-slate-200 rounded">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-900 text-white">
                  <th className="px-2 py-1.5 text-left w-10">#</th>
                  <th className="px-2 py-1.5 text-left">Salary Head</th>
                  {previous && (
                    <th className="px-2 py-1.5 text-right">
                      Previous<br /><span className="text-[10px] font-normal opacity-75">({previous.effective_from_date})</span>
                    </th>
                  )}
                  <th className="px-2 py-1.5 text-right">
                    {previous ? "New" : "Amount"}<br /><span className="text-[10px] font-normal opacity-75">({selected.effective_from_date})</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPONENT_ROWS.map(([key, label], idx) => {
                  const isTotalRow = AUTO_KEYS.has(key);
                  const tone = isTotalRow ? "bg-amber-50 font-bold" : "";
                  return (
                    <tr key={key} className={`border-t border-slate-200 ${tone}`} data-testid={`view-row-${key}`}>
                      <td className="px-2 py-1 text-center text-slate-500">{idx + 1}</td>
                      <td className="px-2 py-1">{label.replace(" (auto)", "")}</td>
                      {previous && (
                        <td className="px-2 py-1 text-right">{fmtINR(previous[key])}</td>
                      )}
                      <td className="px-2 py-1 text-right">{fmtINR(selected[key])}</td>
                    </tr>
                  );
                })}
                {/* Festival/Gift row #20 */}
                {(selected.gift_amount > 0 || previous?.gift_amount > 0) && (
                  <tr className="border-t border-slate-200 bg-red-50 font-semibold" data-testid="view-row-gift_amount">
                    <td className="px-2 py-1 text-center text-red-600">20</td>
                    <td className="px-2 py-1 text-red-700">
                      🎁 Festival / Gift Bonus
                      {selected.gift_description && (
                        <span className="text-[10px] font-normal text-slate-600 ml-1">({selected.gift_description})</span>
                      )}
                    </td>
                    {previous && (
                      <td className="px-2 py-1 text-right">{fmtINR(previous.gift_amount)}</td>
                    )}
                    <td className="px-2 py-1 text-right text-red-700">{fmtINR(selected.gift_amount)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {selected.flexi_basket_notes && (
            <div className="mt-3 text-xs text-slate-600 italic">
              <b className="not-italic">Flexi Basket Note:</b> {selected.flexi_basket_notes}
            </div>
          )}
          {selected.revision_reason && (
            <div className="mt-1 text-xs text-slate-600">
              <b>Revision Reason:</b> {selected.revision_reason}
            </div>
          )}
          {selected.additional_notes && (
            <div className="mt-1 text-xs text-slate-600">
              <b>Additional Notes:</b> {selected.additional_notes}
            </div>
          )}

          {/* Signatures placeholder */}
          <div className="mt-6 grid grid-cols-4 gap-3 text-[10px] uppercase tracking-wider text-slate-500">
            {["Employee Signature", "HR Manager", "Sr. Manager (Ops)", "GM (Ops)"].map((label) => (
              <div key={label} className="border border-slate-300 rounded p-2 h-20 flex flex-col items-center justify-end">
                <span className="text-center">{label}</span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="mt-5 flex flex-wrap gap-2 justify-end pt-3 border-t border-slate-200">
            <Button variant="outline" size="sm" onClick={() => downloadPdf(selected.id)} data-testid="download-pdf-btn">
              <Download className="h-3.5 w-3.5 mr-1.5" /> Download PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-red-200 text-red-700 hover:bg-red-50"
              onClick={() => remove(selected.id)}
              data-testid="delete-record-btn"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
            </Button>
          </div>
        </div>
      )}

      {/* New revision dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="new-revision-dialog">
          <DialogHeader>
            <DialogTitle>Propose New Master Record / Revision</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Revision Type *</Label>
                <Select value={form.revision_description} onValueChange={(v) => setForm({ ...form, revision_description: v })}>
                  <SelectTrigger className="mt-1" data-testid="revision-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REVISION_TYPES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Effective From *</Label>
                <Input
                  type="date"
                  value={form.effective_from_date}
                  onChange={(e) => {
                    const v = e.target.value;
                    // Auto-update next appraisal due (= +12 months) unless user has manually edited it
                    const next = { ...form, effective_from_date: v };
                    try {
                      const d = new Date(v);
                      d.setFullYear(d.getFullYear() + 1);
                      next.next_appraisal_due_date = d.toISOString().slice(0, 10);
                    } catch (_) { /* ignore */ }
                    setForm(next);
                  }}
                  className="mt-1"
                  data-testid="effective-date-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Next Appraisal Due Date</Label>
                <Input
                  type="date"
                  value={form.next_appraisal_due_date}
                  onChange={(e) => setForm({ ...form, next_appraisal_due_date: e.target.value })}
                  className="mt-1"
                  data-testid="next-appraisal-input"
                />
                <p className="text-[10px] text-slate-500 mt-1">Default: Effective + 12 months. Used by HR Appraisal Dashboard + monthly reminder email.</p>
              </div>
              <div className="bg-red-50/40 border border-red-200 rounded p-2">
                <Label className="text-[11px] uppercase tracking-wider text-red-700 font-semibold">🎁 Festival / Gift Bonus</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="number"
                    step="1"
                    placeholder="Amount ₹"
                    value={form.gift_amount}
                    onChange={(e) => setForm({ ...form, gift_amount: e.target.value })}
                    className="h-8 text-xs w-32"
                    data-testid="gift-amount-input"
                  />
                  <Input
                    placeholder="Occasion (e.g. Diwali 2026)"
                    value={form.gift_description}
                    onChange={(e) => setForm({ ...form, gift_description: e.target.value })}
                    className="h-8 text-xs flex-1"
                    data-testid="gift-description-input"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Revision Reason</Label>
              <Textarea
                value={form.revision_reason}
                onChange={(e) => setForm({ ...form, revision_reason: e.target.value })}
                rows={2}
                placeholder="e.g., A grade performance, customer feedback excellent..."
                className="mt-1"
                data-testid="revision-reason-input"
              />
            </div>

            {records.length > 0 && (
              <div className="bg-red-50/40 border border-red-200 rounded p-2 flex items-center gap-2 text-xs">
                <AlertCircle className="h-3.5 w-3.5 text-red-700 flex-shrink-0" />
                <span>Apply quick %:</span>
                <Input
                  type="number"
                  step="0.5"
                  className="h-7 w-20"
                  value={form.percentage}
                  onChange={(e) => setForm({ ...form, percentage: e.target.value })}
                  placeholder="10"
                  data-testid="percentage-input"
                />
                <span>% to all components</span>
                <Button size="sm" variant="outline" onClick={applyPercentage} className="h-6 text-xs" data-testid="apply-pct-btn">
                  Apply
                </Button>
              </div>
            )}
            {records.length === 0 && baseline && baseline.ctc_input > 0 && (
              <div className="bg-blue-50/60 border border-blue-200 rounded p-2 text-xs flex items-center gap-2" data-testid="baseline-banner">
                <AlertCircle className="h-3.5 w-3.5 text-blue-700 flex-shrink-0" />
                <span>
                  "Current" column shows baseline computed from employee's existing CTC of <b>₹{baseline.ctc_input.toLocaleString("en-IN")}</b>
                  &nbsp;(50% Basic / 30% HRA / 20% TA + payroll PF/ESI{baseline.bond_applicable ? "/Bond" : ""}).
                </span>
              </div>
            )}

            {/* Net Salary Target — % or amount */}
            <div className="bg-emerald-50/40 border border-emerald-200 rounded p-2.5" data-testid="net-target-section">
              <Label className="text-[11px] uppercase tracking-wider text-emerald-800 font-semibold flex items-center gap-1">
                💰 Net Salary Target <span className="text-[10px] font-normal text-slate-500 ml-1">(optional · auto-fills "Other Deductions")</span>
              </Label>
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                <Select
                  value={form.net_target_mode || "off"}
                  onValueChange={(v) => setForm({ ...form, net_target_mode: v, net_target_value: "" })}
                >
                  <SelectTrigger className="h-8 w-36 text-xs" data-testid="net-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off">Off (auto net)</SelectItem>
                    <SelectItem value="percent">% of Gross</SelectItem>
                    <SelectItem value="amount">Fixed Amount ₹</SelectItem>
                  </SelectContent>
                </Select>
                {form.net_target_mode && form.net_target_mode !== "off" && (
                  <>
                    <Input
                      type="number"
                      step={form.net_target_mode === "percent" ? "0.1" : "100"}
                      placeholder={form.net_target_mode === "percent" ? "e.g. 85" : "e.g. 35000"}
                      value={form.net_target_value || ""}
                      onChange={(e) => setForm({ ...form, net_target_value: e.target.value })}
                      className="h-8 w-32 text-xs"
                      data-testid="net-target-value"
                    />
                    <span className="text-xs text-slate-600">{form.net_target_mode === "percent" ? "% of Gross" : "₹/month"}</span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => {
                        const gross = formTotals.gross_salary || 0;
                        const targetNet = form.net_target_mode === "percent"
                          ? Math.round(gross * (parseFloat(form.net_target_value || 0) / 100))
                          : parseFloat(form.net_target_value || 0);
                        if (!targetNet || targetNet <= 0) {
                          toast.error("Enter a positive target value");
                          return;
                        }
                        if (targetNet > gross) {
                          toast.error("Target net cannot exceed Gross Salary");
                          return;
                        }
                        // Distribute: subtract existing deductions, fill rest into other_deductions
                        const epf = parseFloat(form.employee_pf_contribution || 0) || 0;
                        const eesi = parseFloat(form.employee_esic_contribution || 0) || 0;
                        const lessRet = parseFloat(form.less_retention_incentive || 0) || 0;
                        const gmc = parseFloat(form.gmc_policy || 0) || 0;
                        const pt = parseFloat(form.professional_tax || 0) || 0;
                        const it = parseFloat(form.income_tax || 0) || 0;
                        const knownDed = epf + eesi + lessRet + gmc + pt + it;
                        const remaining = Math.max(0, gross - targetNet - knownDed);
                        setForm({ ...form, other_deductions: Math.round(remaining) });
                        toast.success(`Net target ₹${targetNet.toLocaleString("en-IN")} applied. Other Deductions = ₹${Math.round(remaining).toLocaleString("en-IN")}`);
                      }}
                      data-testid="apply-net-target-btn"
                    >
                      Apply to Deductions
                    </Button>
                    <span className="text-[10px] text-slate-500 ml-auto">
                      Implied Net: ₹{(form.net_target_mode === "percent"
                        ? Math.round((formTotals.gross_salary || 0) * (parseFloat(form.net_target_value || 0) / 100))
                        : (parseFloat(form.net_target_value || 0) || 0)
                      ).toLocaleString("en-IN")}
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="border border-slate-200 rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-2 py-1.5 text-left w-8">#</th>
                    <th className="px-2 py-1.5 text-left">Component</th>
                    <th className="px-2 py-1.5 text-right w-32">
                      Current
                      {!records[0] && baseline?.ctc_input > 0 && (
                        <span className="block text-[9px] font-normal text-slate-500">(from CTC)</span>
                      )}
                    </th>
                    <th className="px-2 py-1.5 text-right w-36">New</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPONENT_ROWS.map(([key, label], idx) => {
                    const auto = AUTO_KEYS.has(key);
                    const currentSource = records[0] || baseline || {};
                    const current = currentSource[key];
                    const newVal = auto ? formTotals[key] : form[key];
                    return (
                      <tr key={key} className={`border-t border-slate-100 ${auto ? "bg-amber-50/40 font-semibold" : ""}`}>
                        <td className="px-2 py-1 text-center text-slate-500">{idx + 1}</td>
                        <td className="px-2 py-1">{label.replace(" (auto)", "")}</td>
                        <td className="px-2 py-1 text-right text-slate-500">{fmtINR(current)}</td>
                        <td className="px-2 py-1 text-right">
                          {auto ? (
                            <span className="font-mono">{fmtINR(newVal)}</span>
                          ) : (
                            <Input
                              type="number"
                              step="1"
                              value={form[key]}
                              onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                              className="h-7 text-xs text-right"
                              data-testid={`field-${key}`}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {/* Festival/Gift row 20 */}
                  <tr className="border-t border-slate-100 bg-red-50/40">
                    <td className="px-2 py-1 text-center text-red-600 font-semibold">20</td>
                    <td className="px-2 py-1 text-red-700 font-semibold">🎁 Festival / Gift Bonus</td>
                    <td className="px-2 py-1 text-right text-slate-500">{fmtINR(records[0]?.gift_amount)}</td>
                    <td className="px-2 py-1 text-right">
                      <span className="font-mono text-red-700">{form.gift_amount ? fmtINR(form.gift_amount) : "—"}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Delta preview */}
            {(records[0] || baseline) && (
              <div className="bg-emerald-50/40 border border-emerald-200 rounded p-2 text-xs flex flex-wrap items-center gap-3" data-testid="delta-preview">
                {(() => {
                  const ref = records[0] || baseline || {};
                  const refNet = ref.net_payable_monthly || 0;
                  const refCtc = ref.ctc || 0;
                  return (
                    <>
                      <span>
                        Δ Net: <b>{fmtINR(formTotals.net_payable_monthly - refNet)}</b>
                        &nbsp;({refNet ? ((formTotals.net_payable_monthly - refNet) / refNet * 100).toFixed(2) : "0"}%)
                      </span>
                      <span>
                        Δ CTC: <b>{fmtINR(formTotals.ctc - refCtc)}</b>
                      </span>
                      <span className="text-slate-500">
                        Annual Δ: {fmtINR((formTotals.net_payable_monthly - refNet) * 12)}
                      </span>
                    </>
                  );
                })()}
              </div>
            )}

            <div className="grid grid-cols-1 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Flexi Basket Notes</Label>
                <Input
                  value={form.flexi_basket_notes}
                  onChange={(e) => setForm({ ...form, flexi_basket_notes: e.target.value })}
                  placeholder="e.g., Contains Driver Salary, Petrol Reimbursement, LTA…"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Additional Notes</Label>
                <Textarea
                  value={form.additional_notes}
                  onChange={(e) => setForm({ ...form, additional_notes: e.target.value })}
                  rows={2}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="brand-bg brand-bg-hover text-white" data-testid="submit-revision-btn">
              <FileText className="h-3.5 w-3.5 mr-1.5" /> {busy ? "Saving…" : "Save Revision"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="font-semibold text-slate-700 min-w-[120px]">{label}:</span>
      <span className="text-slate-900">{value || "—"}</span>
    </div>
  );
}
